import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/ui/Header';
import QuickActionCard from '@restaurant-manager/frontend/employee-management/employee-self-service/components/QuickActionCard';
import TeamPerformanceOverview from './components/TeamPerformanceOverview';
import ShiftCoverageOverview from './components/ShiftCoverageOverview';
import PendingRequests from './components/PendingRequests';
import OperationalAlerts from './components/OperationalAlerts';
import DailyChecklist from './components/DailyChecklist';
import { useLocations } from '@/context/LocationsContext';
import { useAuth } from '@/context/AuthContext';
import useTranslation from '@/hooks/useTranslation';
import useNotifications from '@/hooks/useNotifications';
import { NotificationSection } from 'shared';

const ManagerSelfService = () => {
  const { t } = useTranslation(['header', 'common', 'managerSelfService']);
  const {
    locations,
    loading: locationsLoading,
    error: locationsError,
    refetch: refetchLocations,
  } = useLocations();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { notifications: userNotifications } = useNotifications(10000);

  const [selectedLocation, setSelectedLocation] = useState(null);

  useEffect(() => {
    if (!Array.isArray(locations) || locations.length === 0) {
      return;
    }

    setSelectedLocation((prev) => {
      if (prev && locations.some((locationItem) => locationItem?.id === prev?.id)) {
        return prev;
      }
      return locations[0];
    });
  }, [locations]);

  const unseenCountsBySection = useMemo(() => {
    return (userNotifications ?? []).reduce((acc, notification) => {
      if (!notification?.seen && notification?.section) {
        acc[notification.section] = (acc[notification.section] || 0) + 1;
      }
      return acc;
    }, {});
  }, [userNotifications]);

  const getBadgeForSection = useCallback(
    (section) => {
      const count = unseenCountsBySection?.[section];
      if (typeof count !== 'number' || count <= 0) {
        return null;
      }
      return count;
    },
    [unseenCountsBySection],
  );

  const notifications = useMemo(
    () => ({
      messages: getBadgeForSection(NotificationSection.INTERNAL_CHAT),
      timeOff: getBadgeForSection(NotificationSection.ABSENCE_MANAGEMENT),
    }),
    [getBadgeForSection],
  );

  const quickActions = useMemo(() => [
    {
      title: t('managerSelfService.actions.employeeManagement.title', 'Employee Management'),
      description: t(
        'managerSelfService.actions.employeeManagement.description',
        'Keep employee records and roles up to date, and jump into onboarding when people join.',
      ),
      iconName: 'Users',
      variant: 'default',
      badge: getBadgeForSection(NotificationSection.STAFF_MANAGEMENT),
      onClick: () => navigate('/employee-directory')
    },
    {
      title: t('managerSelfService.actions.absenceManagement.title', 'Absence Management'),
      description: t(
        'managerSelfService.actions.absenceManagement.description',
        'Review and approve time-off requests quickly while monitoring team coverage for the week.',
      ),
      iconName: 'CalendarCheck',
      variant: 'success',
      badge: getBadgeForSection(NotificationSection.ABSENCE_MANAGEMENT),
      onClick: () => navigate('/time-off-management')
    },
    {
      title: t('managerSelfService.actions.shiftManagement.title', 'Shift Management'),
      description: t(
        'managerSelfService.actions.shiftManagement.description',
        'Plan shifts and cover gaps at a glance so every service is fully staffed.',
      ),
      iconName: 'CalendarCog',
      variant: 'default',
      badge: getBadgeForSection(NotificationSection.SHIFT_MANAGEMENT),
      onClick: () => navigate('/employee-scheduling')
    },
    {
      title: t('managerSelfService.actions.internalMessaging.title', 'Internal Messaging'),
      description: t(
        'managerSelfService.actions.internalMessaging.description',
        'Send quick updates to individuals or teams and keep conversations organized in one place.',
      ),
      iconName: 'MessageSquare',
      variant: 'default',
      badge: getBadgeForSection(NotificationSection.INTERNAL_CHAT),
      onClick: () => navigate('/internal-messaging')
    },
    {
      title: t('managerSelfService.actions.incidentReporting.title', 'Incident Reporting'),
      description: t(
        'managerSelfService.actions.incidentReporting.description',
        'Log workplace issues and track follow-ups so nothing stalls without a response.',
      ),
      iconName: 'ShieldAlert',
      variant: 'error',
      badge: getBadgeForSection(NotificationSection.INCIDENT_MANAGEMENT),
      onClick: () => navigate('/incident-reports')
    },
    {
      title: t('managerSelfService.actions.hiringProcess.title', 'Hiring Process'),
      description: t(
        'managerSelfService.actions.hiringProcess.description',
        'Move candidates through reviews and interviews while keeping every decision documented.',
      ),
      iconName: 'BriefcaseBusiness',
      variant: 'default',
      badge: getBadgeForSection(NotificationSection.HIRING),
      onClick: () => navigate('/hiring')
    },
    {
      title: t('managerSelfService.actions.inJobTraining.title', 'In-Job Training'),
      description: t(
        'managerSelfService.actions.inJobTraining.description',
        'Assign and monitor required training modules and confirm completions as they happen.',
      ),
      iconName: 'LibraryBig',
      variant: 'default',
      badge: getBadgeForSection(NotificationSection.TRAINING_MANAGEMENT),
      onClick: () => navigate('/trainings')
    },
    {
      title: t('managerSelfService.actions.supportSystem.title', 'Support System'),
      description: t(
        'managerSelfService.actions.supportSystem.description',
        'Contact Ougama support for help or ideas and track responses without leaving the dashboard.',
      ),
      iconName: 'Shield',
      variant: 'warning',
      onClick: () => navigate('/support')
    }
  ], [getBadgeForSection, navigate, t]);

  const handleLocationChange = (location) => {
    setSelectedLocation(location ?? null);
  };

  const displayFirstName = useMemo(() => {
    const sources = [
      typeof user?.firstName === 'string' ? user.firstName : null,
      typeof user?.fullName === 'string' ? user.fullName : null,
      typeof user?.name === 'string' ? user.name : null,
      typeof user?.email === 'string' ? user.email.split('@')[0] : null,
    ];

    for (const value of sources) {
      if (!value) {
        continue;
      }
      const trimmed = value.trim();
      if (!trimmed) {
        continue;
      }
      return trimmed.split(/\s+/)[0];
    }

    const fallbackName = t('managerSelfService.greetingFallbackName', 'there');
    return fallbackName;
  }, [user]);

  return (
    <div className="min-h-screen bg-background">
      <Header
        userRole="manager"
        selectedLocation={
          selectedLocation?.name ?? (locationsLoading ? t('header.location.loadingLocations') : '')
        }
        locations={locations}
        onLocationChange={handleLocationChange}
        allowLocationSelection={false}
        notifications={notifications}
      />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {locationsError && (
            <div className="mb-4 bg-error/10 border border-error/20 rounded-lg px-4 py-3 text-sm text-error">
              {t('managerSelfService.errors.locations', 'Unable to load locations.')}
              {' '}
              <button
                type="button"
                className="underline"
                onClick={() => refetchLocations().catch(() => {})}
              >
                {t('common.actions.tryAgain')}
              </button>
            </div>
          )}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              {t('managerSelfService.greeting', 'Welcome back, {{name}}!').replace('{{name}}', displayFirstName)}
            </h1>
            <p className="text-lg text-muted-foreground">
              {t(
                'managerSelfService.subheading',
                'Keep your locations running smoothly with real-time staffing, labor, and guest insights.',
              )}
            </p>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-foreground mb-6">
              {t('managerSelfService.sections.managerActions', 'Manager Actions')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {quickActions.map((action, index) => (
                <QuickActionCard
                  key={index}
                  title={action.title}
                  description={action.description}
                  iconName={action.iconName}
                  variant={action.variant}
                  badge={action.badge}
                  onClick={action.onClick}
                />
              ))}
            </div>
          </div>

          {/* <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
               <TeamPerformanceOverview /> 
               <ShiftCoverageOverview />
              <OperationalAlerts /> 
            </div>
            <div className="space-y-8">
              <PendingRequests /> 
              <DailyChecklist />
            </div>
          </div> */}
        </div>
      </main>
    </div>
  );
};

export default ManagerSelfService;
